'use client'

import { Pagination } from '@/components/ui/pagination'
import { Select } from '@/components/ui/select'
import { stringToSlug } from '@/lib/utils'
import { TPageDetail, TRecruitment } from '@/types/page'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { JobDetailModal } from './job-detail-modal'
import { JobItem } from './job-item'

const ITEM_PER_PAGE = 6

export const JobList = ({ data }: { data?: TPageDetail | null }) => {
  const t = useTranslations()
  const [defaultOpenJob, setDefaultOpenJob] = useState<TRecruitment | null>(
    null
  )
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState({
    department: 'all',
    address: 'all',
  })
  const searchParams = useSearchParams()

  const recruitment =
    data?.detail?.body?.recruitment?.sort((a, b) => a.order - b.order) || []

  const addressOptions = [
    { label: t('landing_coolmate_101_page.recruitment.all'), value: 'all' },
    ...Array.from(new Set(recruitment.map((item) => item.address)))
      .filter(Boolean)
      .map((address) => ({
        label: address,
        value: address,
      })),
  ]

  const departmentOptions = [
    { label: t('landing_coolmate_101_page.recruitment.all'), value: 'all' },
    ...Array.from(new Set(recruitment.map((item) => item.department_details)))
      .filter(Boolean)
      .map((department) => ({
        label: department,
        value: department,
      })),
  ]

  const filteredRecruitment = recruitment.filter((item) => {
    const matchDepartment =
      filters.department === 'all' ||
      item.department_details === filters.department
    const matchAddress =
      filters.address === 'all' || item.address === filters.address
    return matchDepartment && matchAddress
  })

  const totalItems = filteredRecruitment.length
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEM_PER_PAGE))
  const currentPage = Math.min(page, totalPages)
  const startIndex = (currentPage - 1) * ITEM_PER_PAGE
  const endIndex = Math.min(startIndex + ITEM_PER_PAGE, totalItems)
  const paginatedRecruitment = filteredRecruitment.slice(startIndex, endIndex)

  const handleFilterChange = (key: 'department' | 'address', value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }))
    setPage(1)
  }

  useEffect(() => {
    const jobParam = searchParams.get('job')
    const job = recruitment.find(
      (item) => stringToSlug(item.title) === jobParam
    )
    if (job) {
      setDefaultOpenJob(job)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className='mx-auto max-w-screen-2xl'>
      <div className='flex items-center gap-4 pl-10'>
        <div className='max-md:flex-1'>
          <p className='mb-2 text-xs font-medium text-light'>
            {t('landing_coolmate_101_page.recruitment.department')}
          </p>
          <Select
            options={departmentOptions}
            placeholder={t('landing_coolmate_101_page.recruitment.department')}
            value={filters.department}
            onValueChange={(value) => handleFilterChange('department', value)}
            className={{
              trigger: 'w-full text-left lg:w-[224px]',
              content: 'w-full lg:w-[224px]',
            }}
          />
        </div>
        <div className='max-md:flex-1'>
          <p className='mb-2 text-xs font-medium text-light'>
            {t('landing_coolmate_101_page.recruitment.location')}
          </p>
          <Select
            options={addressOptions}
            placeholder={t('landing_coolmate_101_page.recruitment.location')}
            value={filters.address}
            onValueChange={(value) => handleFilterChange('address', value)}
            className={{
              trigger: 'w-full text-left lg:w-[224px]',
              content: 'w-full lg:w-[224px]',
            }}
          />
        </div>
      </div>
      {totalItems > 0 ? (
        <div className='mt-8 grid gap-4 pl-10 sm:grid-cols-2 md:gap-20 lg:grid-cols-3'>
          {paginatedRecruitment.map((item, idx) => (
            <JobItem item={item} key={idx} />
          ))}
        </div>
      ) : (
        <p className='mt-8 text-center text-sm font-medium text-light'>
          {t('landing_coolmate_101_page.recruitment.no_result')}
        </p>
      )}

      {totalItems > 0 && (
        <Pagination
          className='mt-10'
          totalPages={totalPages}
          page={currentPage}
          onPageChange={setPage}
        />
      )}
      {defaultOpenJob && (
        <JobDetailModal
          data={defaultOpenJob}
          isClose={!!defaultOpenJob?.closed}
          isOpenDefault
        />
      )}
    </div>
  )
}
